// ─────────────────────────────────────────────────────────────────────────
// The Spine — TELOS sector survey (path A). Everyday first-person prompts,
// 3-4 options each, votes fold into a SectorSuggestion (top vision, top
// deity, top 3 aims) from shapeCascade.ts. See spine-types.ts for shapes
// and docs/OLMPS_SPINE_SPEC.md §1 / §5 for the question-count rules
// (min 5, max 20 — this bank ships the full 20 per sector).
// ─────────────────────────────────────────────────────────────────────────
import type { Sector } from '../domain/olmps';
import type { SectorSuggestion, SurveyOption, SurveyQuestion } from './spine-types';

// ── S · SOCIAL ──────────────────────────────────────────────────────────

const SOCIAL_SURVEY: SurveyQuestion[] = [
  {
    prompt: 'Which part of your social life is weakest right now?',
    options: [
      { label: 'I don\'t reach out — I let silence grow with people who matter', aimIds: ['s-aim-reachout'], visionIds: ['s-v1'], weight: 3 },
      { label: 'I avoid saying what needs to be said', aimIds: ['s-aim-honesty'], deityIds: ['sofia'], visionIds: ['s-v5'], weight: 3 },
      { label: 'I\'m not growing my circle or the reputation that opens doors', aimIds: ['s-aim-network', 's-aim-reputation'], visionIds: ['s-v2', 's-v4'], weight: 3 },
      { label: 'I give without limits, or I don\'t give at all', aimIds: ['s-aim-give', 's-aim-boundaries'], deityIds: ['kharis'], visionIds: ['s-v3', 's-v6'], weight: 3 },
    ],
  },
  {
    prompt: 'How steady are you at reaching out before the silence gets long?',
    options: [
      { label: 'I basically never reach out first', aimIds: ['s-aim-reachout'], weight: 3 },
      { label: 'I mean to, but I usually let it slide', aimIds: ['s-aim-reachout'], weight: 2 },
      { label: 'I reach out fairly regularly', aimIds: ['s-aim-reachout'], weight: 1 },
      { label: 'I\'m consistent — I reach out before it\'s ever an issue', deityIds: ['sofia'], weight: 1 },
    ],
  },
  {
    prompt: 'How honest are you when a hard conversation is needed?',
    options: [
      { label: 'I avoid it completely', aimIds: ['s-aim-honesty'], deityIds: ['sofia'], weight: 3 },
      { label: 'I bring it up eventually, after it\'s built up', aimIds: ['s-aim-honesty'], weight: 2 },
      { label: 'I usually address it fairly quickly', aimIds: ['s-aim-honesty'], weight: 1 },
      { label: 'I address it directly and calmly, every time', deityIds: ['sofia'], weight: 1 },
    ],
  },
  {
    prompt: 'How balanced are your relationships — what you give versus what you protect?',
    options: [
      { label: 'Mostly one-sided — I give without limits or I never set them', aimIds: ['s-aim-give', 's-aim-boundaries'], weight: 3 },
      { label: 'Sometimes uneven — I notice, but rarely act', aimIds: ['s-aim-boundaries'], weight: 2 },
      { label: 'Fairly balanced, most of the time', aimIds: ['s-aim-give'], weight: 1 },
      { label: 'Balanced and protected — I give freely and hold my line', deityIds: ['kharis'], weight: 1 },
    ],
  },
  {
    prompt: 'What would make a week feel like a social win?',
    options: [
      { label: 'I reached out before someone had to reach out to me', aimIds: ['s-aim-reachout'], visionIds: ['s-v1'], weight: 3 },
      { label: 'I said what needed to be said, even if it was uncomfortable', aimIds: ['s-aim-honesty'], deityIds: ['sofia'], weight: 3 },
      { label: 'I met someone new worth knowing', aimIds: ['s-aim-network'], visionIds: ['s-v2'], weight: 3 },
      { label: 'I said no to something that would have drained me', aimIds: ['s-aim-boundaries'], weight: 3 },
    ],
  },
  {
    prompt: 'What kind of reach matters most to you right now?',
    options: [
      { label: 'Being heard clearly when it counts', deityIds: ['sofia'], visionIds: ['s-v2'], weight: 3 },
      { label: 'Being trusted by the people already close to me', deityIds: ['kharis'], visionIds: ['s-v1'], weight: 3 },
      { label: 'Expanding who I even know', aimIds: ['s-aim-network'], weight: 2 },
    ],
  },
  {
    prompt: 'Think of the last time you gave someone your full attention. When was it?',
    options: [
      { label: 'Recently, and it felt good', aimIds: ['s-aim-give'], deityIds: ['kharis'], weight: 2 },
      { label: 'I honestly can\'t remember', aimIds: ['s-aim-reachout'], weight: 3 },
      { label: 'I\'m usually half-present, phone in hand', aimIds: ['s-aim-give'], weight: 3 },
    ],
  },
  {
    prompt: 'What would "having a reputation that outlasts me" actually require?',
    options: [
      { label: 'Delivering on what I say I\'ll do', aimIds: ['s-aim-reputation'], visionIds: ['s-v4'], weight: 3 },
      { label: 'Being someone people can be honest with', aimIds: ['s-aim-honesty'], visionIds: ['s-v5'], weight: 3 },
      { label: 'Building a wider network on purpose', aimIds: ['s-aim-network'], weight: 2 },
    ],
  },
  {
    prompt: 'How comfortable are you asking a real question instead of a polite one?',
    options: [
      { label: 'Not very — I default to small talk', aimIds: ['s-aim-honesty'], deityIds: ['sofia'], weight: 3 },
      { label: 'Depends who I\'m with', aimIds: ['s-aim-honesty'], weight: 1 },
      { label: 'Pretty comfortable, I go there fast', deityIds: ['sofia'], weight: 1 },
    ],
  },
  {
    prompt: 'Do you have relationships that are one-sided right now?',
    options: [
      { label: 'Yes, and I know exactly which ones', aimIds: ['s-aim-boundaries'], weight: 3 },
      { label: 'Maybe — I\'ve never actually checked', aimIds: ['s-aim-boundaries'], weight: 2 },
      { label: 'No, things feel fairly balanced', aimIds: ['s-aim-give'], weight: 1 },
    ],
  },
  {
    prompt: 'What matters more to you this season?',
    options: [
      { label: 'Deepening the bonds I already have', visionIds: ['s-v1'], deityIds: ['kharis'], weight: 3 },
      { label: 'Growing my reach and reputation', visionIds: ['s-v2'], deityIds: ['sofia'], weight: 3 },
      { label: 'Building a circle rooted in real trust', visionIds: ['s-v3'], weight: 3 },
    ],
  },
  {
    prompt: 'When a misunderstanding happens with someone close to you, what do you do?',
    options: [
      { label: 'Let it sit and hope it resolves itself', aimIds: ['s-aim-honesty'], weight: 3 },
      { label: 'Address it directly, soon', aimIds: ['s-aim-reputation'], deityIds: ['sofia'], weight: 2 },
      { label: 'Bring it up but soften it too much to land', aimIds: ['s-aim-honesty'], weight: 2 },
    ],
  },
  {
    prompt: 'How do you feel about the size of your network professionally or socially?',
    options: [
      { label: 'Too small — I need to meet more people', aimIds: ['s-aim-network'], visionIds: ['s-v6'], weight: 3 },
      { label: 'Fine in size, weak in depth', aimIds: ['s-aim-give'], weight: 2 },
      { label: 'I\'m happy with it, I just need to maintain it', aimIds: ['s-aim-reachout'], weight: 1 },
    ],
  },
  {
    prompt: 'What does generosity look like for you, honestly?',
    options: [
      { label: 'Something I want to give more of, without keeping score', aimIds: ['s-aim-give'], deityIds: ['kharis'], weight: 3 },
      { label: 'Something I already do, but no one reciprocates', aimIds: ['s-aim-boundaries'], weight: 2 },
      { label: 'Not something I think about much', visionIds: ['s-v3'], weight: 1 },
    ],
  },
  {
    prompt: 'When you commit to something socially, how often do you follow through?',
    options: [
      { label: 'Honestly, less than I\'d like', aimIds: ['s-aim-reputation'], weight: 3 },
      { label: 'Mostly, but people notice the exceptions', aimIds: ['s-aim-reputation'], weight: 2 },
      { label: 'Reliably — it\'s part of who I am', deityIds: ['sofia'], weight: 1 },
    ],
  },
  {
    prompt: 'What\'s the harder skill for you — speaking up or listening?',
    options: [
      { label: 'Speaking up, I avoid friction', aimIds: ['s-aim-honesty'], deityIds: ['sofia'], weight: 3 },
      { label: 'Listening, I\'m usually thinking about my reply', deityIds: ['sofia'], weight: 3 },
      { label: 'Neither — presence is what I lack', aimIds: ['s-aim-give'], weight: 2 },
    ],
  },
  {
    prompt: 'Picture your ideal social life five years from now. What stands out?',
    options: [
      { label: 'A small circle that has been through everything with me', visionIds: ['s-v1'], weight: 3 },
      { label: 'A wide, respected network I built deliberately', visionIds: ['s-v2'], weight: 3 },
      { label: 'Relationships that survived hard truths intact', visionIds: ['s-v5'], weight: 3 },
    ],
  },
  {
    prompt: 'Do you have a standing ritual with anyone close to you (call, meal, walk)?',
    options: [
      { label: 'No, and I miss having one', aimIds: ['s-aim-give'], deityIds: ['kharis'], weight: 3 },
      { label: 'Used to, it faded', aimIds: ['s-aim-reachout'], weight: 2 },
      { label: 'Yes, and I protect it', deityIds: ['kharis'], weight: 1 },
    ],
  },
  {
    prompt: 'What would you regret most in ten years, socially?',
    options: [
      { label: 'Not having stayed close to the people who mattered', visionIds: ['s-v1'], aimIds: ['s-aim-reachout'], weight: 3 },
      { label: 'Never having said the things that needed saying', visionIds: ['s-v5'], aimIds: ['s-aim-honesty'], weight: 3 },
      { label: 'Never having built the reputation I wanted', visionIds: ['s-v4'], aimIds: ['s-aim-reputation'], weight: 3 },
    ],
  },
  {
    prompt: 'Which deity\'s domain feels most missing from your life right now?',
    options: [
      { label: 'The word that lands — being heard and understood', deityIds: ['sofia'], weight: 3 },
      { label: 'Generosity that comes back — real, mutual bonds', deityIds: ['kharis'], weight: 3 },
      { label: 'Neither — I want to grow both equally', deityIds: ['sofia'], weight: 1 },
    ],
  },
];

// ── H · HEALTH ──────────────────────────────────────────────────────────

const HEALTH_SURVEY: SurveyQuestion[] = [
  {
    prompt: 'Which part of your physical health is weakest right now?',
    options: [
      { label: 'My sleep and recovery are the weak link', aimIds: ['h-aim-sleep', 'h-aim-recovery'], deityIds: ['hypnos'], visionIds: ['h-v3', 'h-v5'], weight: 3 },
      { label: 'My eating and how I maintain my body are off', aimIds: ['h-aim-eat', 'h-aim-hygiene'], deityIds: ['hygia'], visionIds: ['h-v4', 'h-v6'], weight: 3 },
      { label: 'An injury or health issue I\'m ignoring is the real problem', aimIds: ['h-aim-checkup'], deityIds: ['akeso'], visionIds: ['h-v1', 'h-v2'], weight: 3 },
      { label: 'I just don\'t move enough, period', aimIds: ['h-aim-move'], weight: 3 },
    ],
  },
  {
    prompt: 'How steady is your sleep, honestly?',
    options: [
      { label: 'A mess — different every night, no real target', aimIds: ['h-aim-sleep'], deityIds: ['hypnos'], weight: 3 },
      { label: 'Inconsistent — I mean to hold a schedule but don\'t', aimIds: ['h-aim-sleep'], weight: 2 },
      { label: 'Mostly steady, close to a target time', aimIds: ['h-aim-sleep'], weight: 1 },
      { label: 'Dialed in — fixed time, every night', deityIds: ['hypnos'], weight: 1 },
    ],
  },
  {
    prompt: 'How consistent is your eating and hydration?',
    options: [
      { label: 'I eat whatever is around, no real plan', aimIds: ['h-aim-eat'], deityIds: ['hygia'], weight: 3 },
      { label: 'A few solid days, then it slips', aimIds: ['h-aim-eat'], weight: 2 },
      { label: 'Mostly consistent, most days', aimIds: ['h-aim-eat'], weight: 1 },
      { label: 'Dialed in — planned and tracked', deityIds: ['hygia'], weight: 1 },
    ],
  },
  {
    prompt: 'How intentional is your movement and recovery day to day?',
    options: [
      { label: 'Basically zero structure — I sit all day and never truly rest', aimIds: ['h-aim-move', 'h-aim-recovery'], weight: 3 },
      { label: 'I move or rest, but without any real plan', aimIds: ['h-aim-move'], weight: 2 },
      { label: 'I have some structure and mostly follow it', aimIds: ['h-aim-recovery'], weight: 1 },
      { label: 'Dialed in — trained and rested on a real plan', deityIds: ['hypnos'], weight: 1 },
    ],
  },
  {
    prompt: 'What is most likely to derail your day physically?',
    options: [
      { label: 'A bad night\'s sleep', aimIds: ['h-aim-sleep'], deityIds: ['hypnos'], weight: 3 },
      { label: 'Not eating or drinking water properly', aimIds: ['h-aim-eat'], deityIds: ['hygia'], weight: 3 },
      { label: 'Pain or a nagging injury I\'m ignoring', aimIds: ['h-aim-recovery'], deityIds: ['akeso'], weight: 3 },
      { label: 'Just not moving at all', aimIds: ['h-aim-move'], weight: 3 },
    ],
  },
  {
    prompt: 'What does "energy that is earned, not borrowed" mean to your current life?',
    options: [
      { label: 'I\'m running on caffeine and willpower', visionIds: ['h-v3'], deityIds: ['hypnos'], weight: 3 },
      { label: 'I push through instead of resting properly', visionIds: ['h-v3'], aimIds: ['h-aim-recovery'], weight: 3 },
      { label: 'I already run mostly on real reserves', visionIds: ['h-v3'], weight: 1 },
    ],
  },
  {
    prompt: 'How intentional is your movement day to day?',
    options: [
      { label: 'Basically zero — I sit all day', aimIds: ['h-aim-move'], weight: 3 },
      { label: 'I move but without any real structure', aimIds: ['h-aim-move'], weight: 2 },
      { label: 'I train with a plan', deityIds: ['hygia'], weight: 1 },
    ],
  },
  {
    prompt: 'What does your living/hygiene space say about you right now?',
    options: [
      { label: 'It\'s slipping and I know it', aimIds: ['h-aim-hygiene'], weight: 3 },
      { label: 'It\'s fine but not a priority', aimIds: ['h-aim-hygiene'], weight: 1 },
      { label: 'It\'s dialed in', visionIds: ['h-v4'], weight: 1 },
    ],
  },
  {
    prompt: 'Do you take a full rest day, with zero training load, at least once a week?',
    options: [
      { label: 'Never, I don\'t really rest', aimIds: ['h-aim-recovery'], deityIds: ['hypnos'], weight: 3 },
      { label: 'Occasionally, by accident', aimIds: ['h-aim-recovery'], weight: 2 },
      { label: 'Yes, it\'s scheduled', deityIds: ['hypnos'], weight: 1 },
    ],
  },
  {
    prompt: 'When you get injured or strained, what usually happens?',
    options: [
      { label: 'I push through it and it gets worse', deityIds: ['akeso'], aimIds: ['h-aim-recovery'], weight: 3 },
      { label: 'I rest it but never properly rehab it', deityIds: ['akeso'], weight: 3 },
      { label: 'I follow a real recovery protocol', deityIds: ['akeso'], weight: 1 },
    ],
  },
  {
    prompt: 'How would you describe your relationship with screens before bed?',
    options: [
      { label: 'Scrolling until I fall asleep', aimIds: ['h-aim-sleep'], deityIds: ['hypnos'], weight: 3 },
      { label: 'I try to cut off but don\'t always manage', aimIds: ['h-aim-sleep'], weight: 2 },
      { label: 'Screens off well before bed, consistently', deityIds: ['hypnos'], weight: 1 },
    ],
  },
  {
    prompt: 'What would you most want your body to be capable of that it currently isn\'t?',
    options: [
      { label: 'Handling physical demands without hesitation', visionIds: ['h-v1'], weight: 3 },
      { label: 'Performing when it actually counts, on demand', visionIds: ['h-v6'], weight: 3 },
      { label: 'Just not limiting what I want to do in life', visionIds: ['h-v2'], weight: 3 },
    ],
  },
  {
    prompt: 'How closely do you track vitamins, meds, or supplements you\'re supposed to take?',
    options: [
      { label: 'Not closely, I forget often', aimIds: ['h-aim-checkup'], weight: 3 },
      { label: 'Mostly, with occasional misses', aimIds: ['h-aim-checkup'], weight: 1 },
      { label: 'On schedule, every time', deityIds: ['akeso'], weight: 1 },
    ],
  },
  {
    prompt: 'Do you know a specific body metric you should be tracking right now?',
    options: [
      { label: 'No idea, I don\'t track anything', aimIds: ['h-aim-eat'], deityIds: ['hygia'], weight: 3 },
      { label: 'I know it but don\'t log it', deityIds: ['hygia'], weight: 2 },
      { label: 'Yes, and I log it regularly', deityIds: ['hygia'], weight: 1 },
    ],
  },
  {
    prompt: 'What best describes your maintenance habits — hygiene, grooming, upkeep?',
    options: [
      { label: 'Whatever gets done gets done', aimIds: ['h-aim-hygiene'], weight: 3 },
      { label: 'A basic routine I stick to', aimIds: ['h-aim-hygiene'], weight: 1 },
      { label: 'Meticulous, I take real pride in it', visionIds: ['h-v4'], weight: 1 },
    ],
  },
  {
    prompt: 'If you had to fix one thing about your health this month, what would it be?',
    options: [
      { label: 'My sleep', deityIds: ['hypnos'], aimIds: ['h-aim-sleep'], weight: 3 },
      { label: 'What and how I eat', deityIds: ['hygia'], aimIds: ['h-aim-eat'], weight: 3 },
      { label: 'A nagging injury or pain', deityIds: ['akeso'], aimIds: ['h-aim-recovery'], weight: 3 },
      { label: 'Just moving more, period', aimIds: ['h-aim-move'], weight: 3 },
    ],
  },
  {
    prompt: 'How do you handle sleep when traveling or life gets disrupted?',
    options: [
      { label: 'It falls apart completely', deityIds: ['hypnos'], weight: 3 },
      { label: 'I try to hold roughly the same schedule', deityIds: ['hypnos'], weight: 1 },
      { label: 'I protect it deliberately, wherever I am', deityIds: ['hypnos'], weight: 1 },
    ],
  },
  {
    prompt: 'Do you have a wind-down ritual before sleep that you actually run?',
    options: [
      { label: 'No, I just eventually pass out', aimIds: ['h-aim-sleep'], deityIds: ['hypnos'], weight: 3 },
      { label: 'Loosely, some nights', aimIds: ['h-aim-sleep'], weight: 1 },
      { label: 'Yes, every night, same order', deityIds: ['hypnos'], weight: 1 },
    ],
  },
  {
    prompt: 'What does maintaining "a machine I respect" look like to you?',
    options: [
      { label: 'Treating recovery as seriously as effort', visionIds: ['h-v4'], deityIds: ['hypnos'], weight: 3 },
      { label: 'Fueling it properly instead of neglecting it', visionIds: ['h-v4'], deityIds: ['hygia'], weight: 3 },
      { label: 'Fixing small issues before they become big ones', visionIds: ['h-v4'], deityIds: ['akeso'], weight: 3 },
    ],
  },
  {
    prompt: 'Which of these feels like the biggest gap between where you are and where you want to be?',
    options: [
      { label: 'Rest and recovery', deityIds: ['hypnos'], weight: 3 },
      { label: 'Nutrition and body maintenance', deityIds: ['hygia'], weight: 3 },
      { label: 'Healing and injury management', deityIds: ['akeso'], weight: 3 },
    ],
  },
];

// ── A · ADMIN ───────────────────────────────────────────────────────────

const ADMIN_SURVEY: SurveyQuestion[] = [
  {
    prompt: 'Which part of your admin life is weakest right now?',
    options: [
      { label: 'Inbox and bills are piling up on me', aimIds: ['a-aim-inbox', 'a-aim-bills'], deityIds: ['eunomia'], visionIds: ['a-v3', 'a-v5'], weight: 3 },
      { label: 'My space and systems are a mess — I solve the same problem twice', aimIds: ['a-aim-environment', 'a-aim-systems'], visionIds: ['a-v1', 'a-v6'], weight: 3 },
      { label: 'My records and documents aren\'t something I trust or could find', aimIds: ['a-aim-records'], deityIds: ['themistas'], visionIds: ['a-v2', 'a-v4'], weight: 3 },
      { label: 'Deadlines keep catching me off guard', aimIds: ['a-aim-deadlines'], weight: 3 },
    ],
  },
  {
    prompt: 'How steady are you at keeping your inbox and bills current?',
    options: [
      { label: 'Out of control — I avoid looking', aimIds: ['a-aim-inbox', 'a-aim-bills'], deityIds: ['eunomia'], weight: 3 },
      { label: 'Manageable but never actually zero', aimIds: ['a-aim-inbox'], weight: 2 },
      { label: 'Mostly current, small gaps', aimIds: ['a-aim-bills'], weight: 1 },
      { label: 'Dialed in — basically clear, always', deityIds: ['eunomia'], weight: 1 },
    ],
  },
  {
    prompt: 'Do you have real systems, or are you solving the same problem every time?',
    options: [
      { label: 'No system — I rebuild the wheel constantly', aimIds: ['a-aim-systems'], deityIds: ['eunomia'], weight: 3 },
      { label: 'Some systems, used inconsistently', aimIds: ['a-aim-systems'], weight: 2 },
      { label: 'Real systems, mostly followed', aimIds: ['a-aim-systems'], weight: 1 },
      { label: 'Dialed in — automated wherever it makes sense', deityIds: ['eunomia'], weight: 1 },
    ],
  },
  {
    prompt: 'How trustworthy are your records and documentation, honestly?',
    options: [
      { label: 'Loose papers, scattered files, nothing tracked', aimIds: ['a-aim-records'], deityIds: ['themistas'], weight: 3 },
      { label: 'Half-organized, I could find most of it eventually', aimIds: ['a-aim-records'], weight: 2 },
      { label: 'Mostly organized and current', aimIds: ['a-aim-records'], weight: 1 },
      { label: 'Dialed in — everything documented and findable in a minute', deityIds: ['themistas'], weight: 1 },
    ],
  },
  {
    prompt: 'What\'s the state of your inbox or mail pile right now?',
    options: [
      { label: 'Out of control, I avoid looking', aimIds: ['a-aim-inbox'], deityIds: ['eunomia'], weight: 3 },
      { label: 'Manageable but not zero', aimIds: ['a-aim-inbox'], weight: 2 },
      { label: 'Basically clear', deityIds: ['eunomia'], weight: 1 },
    ],
  },
  {
    prompt: 'How do you feel about deadlines in your life right now?',
    options: [
      { label: 'I\'m always scrambling at the last minute', aimIds: ['a-aim-deadlines'], weight: 3 },
      { label: 'I usually make it, but it\'s close', aimIds: ['a-aim-deadlines'], weight: 2 },
      { label: 'I\'m ahead of most of them', deityIds: ['eunomia'], weight: 1 },
    ],
  },
  {
    prompt: 'If something important happened to you tomorrow, could someone else find your key documents?',
    options: [
      { label: 'No, only I know where anything is', aimIds: ['a-aim-records'], deityIds: ['themistas'], weight: 3 },
      { label: 'They\'d find some of it eventually', aimIds: ['a-aim-records'], weight: 2 },
      { label: 'Yes, it\'s all documented clearly', deityIds: ['themistas'], weight: 1 },
    ],
  },
  {
    prompt: 'What does "having systems that run without my attention" mean to you right now?',
    options: [
      { label: 'A distant dream — everything needs me', visionIds: ['a-v1'], deityIds: ['eunomia'], weight: 3 },
      { label: 'Something I\'ve started but not finished', visionIds: ['a-v1'], weight: 2 },
      { label: 'Already mostly true', visionIds: ['a-v1'], weight: 1 },
    ],
  },
  {
    prompt: 'Do you read the fine print before signing or agreeing to something?',
    options: [
      { label: 'Rarely, I just sign', aimIds: ['a-aim-records'], deityIds: ['themistas'], weight: 3 },
      { label: 'Skim it', aimIds: ['a-aim-records'], weight: 1 },
      { label: 'Always, fully', deityIds: ['themistas'], weight: 1 },
    ],
  },
  {
    prompt: 'How many subscriptions or renewals do you think you\'re paying for but not using?',
    options: [
      { label: 'Probably several, I haven\'t checked', aimIds: ['a-aim-bills'], deityIds: ['eunomia'], weight: 3 },
      { label: 'Maybe one or two', aimIds: ['a-aim-bills'], weight: 1 },
      { label: 'None, I audit regularly', deityIds: ['eunomia'], weight: 1 },
    ],
  },
  {
    prompt: 'What would surprise you most if you opened a maintenance or repair list right now?',
    options: [
      { label: 'How long some of it has been ignored', aimIds: ['a-aim-environment'], weight: 3 },
      { label: 'That there\'s more than I remembered', aimIds: ['a-aim-environment'], weight: 2 },
      { label: 'Nothing, I stay on top of it', visionIds: ['a-v5'], weight: 1 },
    ],
  },
  {
    prompt: 'What best describes how you feel about admin and paperwork generally?',
    options: [
      { label: 'It piles up until it becomes urgent', visionIds: ['a-v3'], weight: 3 },
      { label: 'I handle it but it takes a lot out of me', visionIds: ['a-v3'], weight: 2 },
      { label: 'I trust my systems enough to stop checking', visionIds: ['a-v4'], weight: 1 },
    ],
  },
  {
    prompt: 'Have you ever had a compliance or paperwork dispute you had to scramble to resolve?',
    options: [
      { label: 'Yes, and it was stressful', aimIds: ['a-aim-records'], deityIds: ['themistas'], weight: 3 },
      { label: 'A minor one', aimIds: ['a-aim-records'], weight: 1 },
      { label: 'No, I stay ahead of it', deityIds: ['themistas'], weight: 1 },
    ],
  },
  {
    prompt: 'Do you delegate or automate repeatable admin tasks, or do them all by hand every time?',
    options: [
      { label: 'By hand, every single time', aimIds: ['a-aim-systems'], deityIds: ['eunomia'], weight: 3 },
      { label: 'Some automation, not much', aimIds: ['a-aim-systems'], weight: 2 },
      { label: 'Automated wherever it makes sense', deityIds: ['eunomia'], weight: 1 },
    ],
  },
  {
    prompt: 'What\'s your honest reaction to the word "budget review" or "weekly admin review"?',
    options: [
      { label: 'Dread — I avoid it', aimIds: ['a-aim-systems'], weight: 3 },
      { label: 'I do it, unevenly', aimIds: ['a-aim-systems'], weight: 2 },
      { label: 'It\'s a locked-in weekly habit', deityIds: ['eunomia'], weight: 1 },
    ],
  },
  {
    prompt: 'How would you describe your filing — physical or digital — of important documents?',
    options: [
      { label: 'Loose papers and scattered files', aimIds: ['a-aim-records'], weight: 3 },
      { label: 'Half-organized', aimIds: ['a-aim-records'], weight: 2 },
      { label: 'Everything has a place I can find in a minute', deityIds: ['themistas'], weight: 1 },
    ],
  },
  {
    prompt: 'When a deadline appears, what\'s your first move?',
    options: [
      { label: 'I note it mentally and hope I remember', aimIds: ['a-aim-deadlines'], weight: 3 },
      { label: 'I add it to a list, no buffer', aimIds: ['a-aim-deadlines'], weight: 2 },
      { label: 'It goes straight into a calendar with lead time', deityIds: ['eunomia'], weight: 1 },
    ],
  },
  {
    prompt: 'What would "order I trust enough to stop checking" require from you right now?',
    options: [
      { label: 'Building the systems in the first place', visionIds: ['a-v4'], deityIds: ['eunomia'], weight: 3 },
      { label: 'Documenting everything properly', visionIds: ['a-v4'], deityIds: ['themistas'], weight: 3 },
      { label: 'Just following through on the systems I already have', visionIds: ['a-v4'], weight: 1 },
    ],
  },
  {
    prompt: 'How often does something administrative catch you off guard?',
    options: [
      { label: 'Regularly, and it\'s stressful every time', aimIds: ['a-aim-deadlines'], visionIds: ['a-v2'], weight: 3 },
      { label: 'Occasionally', aimIds: ['a-aim-deadlines'], weight: 2 },
      { label: 'Rarely, I track everything', visionIds: ['a-v2'], weight: 1 },
    ],
  },
  {
    prompt: 'Which sector deity\'s domain feels most needed in your life right now?',
    options: [
      { label: 'Good order — systems and logistics that just run', deityIds: ['eunomia'], weight: 3 },
      { label: 'Documentation — records and compliance I can trust', deityIds: ['themistas'], weight: 3 },
      { label: 'Both equally', deityIds: ['eunomia'], weight: 1 },
    ],
  },
];

// ── P · PERSONAL ───────────────────────────────────────────────────────

const PERSONAL_SURVEY: SurveyQuestion[] = [
  {
    prompt: 'Which part of your inner life is weakest right now?',
    options: [
      { label: 'I don\'t practice or build any skill on purpose', aimIds: ['p-aim-learn', 'p-aim-skill'], deityIds: ['mnemosyne'], visionIds: ['p-v4', 'p-v6'], weight: 3 },
      { label: 'I don\'t reflect or make anything — I just consume and react', aimIds: ['p-aim-reflect', 'p-aim-create'], visionIds: ['p-v2', 'p-v5'], weight: 3 },
      { label: 'I\'m scattered and reactive with my emotions', aimIds: ['p-aim-emotion'], deityIds: ['psyche'], visionIds: ['p-v1', 'p-v3'], weight: 3 },
      { label: 'I don\'t protect any quiet, grounded time at all', aimIds: ['p-aim-ground'], weight: 3 },
    ],
  },
  {
    prompt: 'How consistently do you actually practice a skill, not just consume content about it?',
    options: [
      { label: 'It\'s been a long while since I practiced anything real', aimIds: ['p-aim-learn'], deityIds: ['mnemosyne'], weight: 3 },
      { label: 'I start, but keep stalling', aimIds: ['p-aim-learn', 'p-aim-skill'], weight: 2 },
      { label: 'Fairly regular, most weeks', aimIds: ['p-aim-skill'], weight: 1 },
      { label: 'Dialed in — a real plan, followed', deityIds: ['mnemosyne'], weight: 1 },
    ],
  },
  {
    prompt: 'How steady is your habit of reflecting or journaling on your day?',
    options: [
      { label: 'Nonexistent — I don\'t process my days at all', aimIds: ['p-aim-reflect'], deityIds: ['psyche'], weight: 3 },
      { label: 'Only when things feel heavy', aimIds: ['p-aim-reflect'], weight: 2 },
      { label: 'Fairly regular, most days', aimIds: ['p-aim-reflect'], weight: 1 },
      { label: 'Dialed in — a daily practice', deityIds: ['psyche'], weight: 1 },
    ],
  },
  {
    prompt: 'How grounded and in control of your reactions do you feel day to day?',
    options: [
      { label: 'Scattered — I react before I even notice', aimIds: ['p-aim-ground', 'p-aim-emotion'], weight: 3 },
      { label: 'Okay, but I don\'t protect quiet time or catch myself consistently', aimIds: ['p-aim-ground'], weight: 2 },
      { label: 'Fairly grounded, most days', aimIds: ['p-aim-emotion'], weight: 1 },
      { label: 'Dialed in — a real grounding practice I hold to', deityIds: ['psyche'], weight: 1 },
    ],
  },
  {
    prompt: 'When was the last time you deliberately practiced a skill, not just consumed content about it?',
    options: [
      { label: 'It\'s been a while', aimIds: ['p-aim-learn'], deityIds: ['mnemosyne'], weight: 3 },
      { label: 'Recently, but not consistently', aimIds: ['p-aim-learn'], weight: 2 },
      { label: 'It\'s a regular habit', deityIds: ['mnemosyne'], weight: 1 },
    ],
  },
  {
    prompt: 'Is there a skill you keep saying you\'ll get serious about but haven\'t?',
    options: [
      { label: 'Yes, for a long time now', aimIds: ['p-aim-skill'], deityIds: ['mnemosyne'], weight: 3 },
      { label: 'I\'ve started but keep stalling', aimIds: ['p-aim-skill'], weight: 2 },
      { label: 'No, I follow through on what I start', deityIds: ['mnemosyne'], weight: 1 },
    ],
  },
  {
    prompt: 'When you look back at a pattern that keeps repeating in your life, what do you do?',
    options: [
      { label: 'Notice it, but nothing changes', aimIds: ['p-aim-reflect'], deityIds: ['psyche'], weight: 3 },
      { label: 'Try to change it, inconsistently', aimIds: ['p-aim-reflect'], weight: 2 },
      { label: 'Name it and actively work on it', deityIds: ['psyche'], weight: 1 },
    ],
  },
  {
    prompt: 'What best describes your relationship with discomfort and growth?',
    options: [
      { label: 'I avoid it whenever I can', visionIds: ['p-v2'], deityIds: ['mnemosyne'], weight: 3 },
      { label: 'I tolerate it when forced to', visionIds: ['p-v2'], weight: 2 },
      { label: 'I seek it out on purpose', visionIds: ['p-v3'], weight: 1 },
    ],
  },
  {
    prompt: 'How clear are you, right now, on who you are and what you stand for?',
    options: [
      { label: 'Honestly, pretty unclear', visionIds: ['p-v2'], deityIds: ['psyche'], weight: 3 },
      { label: 'Getting clearer, still working on it', visionIds: ['p-v3'], weight: 2 },
      { label: 'Very clear, and I built it deliberately', visionIds: ['p-v4'], weight: 1 },
    ],
  },
  {
    prompt: 'Do you have a creative outlet you actually use regularly?',
    options: [
      { label: 'No, I used to but stopped', aimIds: ['p-aim-create'], weight: 3 },
      { label: 'Occasionally, when I have time', aimIds: ['p-aim-create'], weight: 2 },
      { label: 'Yes, it\'s protected time', visionIds: ['p-v5'], weight: 1 },
    ],
  },
  {
    prompt: 'How do you learn best, and are you actually doing it that way?',
    options: [
      { label: 'I know how, but I don\'t follow through', aimIds: ['p-aim-learn'], deityIds: ['mnemosyne'], weight: 3 },
      { label: 'I dabble across a lot of things', aimIds: ['p-aim-skill'], weight: 2 },
      { label: 'I have a real plan and stick to it', deityIds: ['mnemosyne'], weight: 1 },
    ],
  },
  {
    prompt: 'When was the last time you sat in silence with no distraction?',
    options: [
      { label: 'I genuinely can\'t remember', aimIds: ['p-aim-ground'], deityIds: ['psyche'], weight: 3 },
      { label: 'It happens by accident sometimes', aimIds: ['p-aim-ground'], weight: 2 },
      { label: 'Regularly, it\'s intentional', deityIds: ['psyche'], weight: 1 },
    ],
  },
  {
    prompt: 'What does "an inner life that drives everything else forward" mean for you right now?',
    options: [
      { label: 'Something missing — I run on autopilot', visionIds: ['p-v5'], deityIds: ['psyche'], weight: 3 },
      { label: 'Something I\'m building slowly', visionIds: ['p-v5'], weight: 2 },
      { label: 'Already true — it\'s my foundation', visionIds: ['p-v5'], weight: 1 },
    ],
  },
  {
    prompt: 'Do you apply what you learn, or does it stay theoretical?',
    options: [
      { label: 'Mostly theoretical, I don\'t act on it', aimIds: ['p-aim-learn'], weight: 3 },
      { label: 'Sometimes I apply it', aimIds: ['p-aim-learn'], weight: 2 },
      { label: 'I apply it fast, that\'s the point', deityIds: ['mnemosyne'], weight: 1 },
    ],
  },
  {
    prompt: 'How honest are you with yourself about your own decisions?',
    options: [
      { label: 'I avoid looking too closely', aimIds: ['p-aim-reflect'], weight: 3 },
      { label: 'I try, but I gloss over the hard parts', aimIds: ['p-aim-reflect'], weight: 2 },
      { label: 'Brutally honest, on purpose', deityIds: ['psyche'], weight: 1 },
    ],
  },
  {
    prompt: 'What kind of self-ownership feels furthest away right now?',
    options: [
      { label: 'Ownership of my emotional reactions', visionIds: ['p-v3'], aimIds: ['p-aim-emotion'], weight: 3 },
      { label: 'Ownership of what I actually do with my time', visionIds: ['p-v4'], aimIds: ['p-aim-create'], weight: 3 },
      { label: 'Ownership of what I know and can do', visionIds: ['p-v6'], aimIds: ['p-aim-skill'], weight: 3 },
    ],
  },
  {
    prompt: 'If you picked one skill to master this year, would you actually follow a real plan for it?',
    options: [
      { label: 'No, I\'d probably drift like usual', aimIds: ['p-aim-skill'], deityIds: ['mnemosyne'], weight: 3 },
      { label: 'I\'d try, but structure isn\'t my strength', aimIds: ['p-aim-skill'], weight: 2 },
      { label: 'Yes, that\'s exactly how I operate', deityIds: ['mnemosyne'], weight: 1 },
    ],
  },
  {
    prompt: 'Do you make things (write, build, design, compose) or mostly consume what others make?',
    options: [
      { label: 'Mostly consume', aimIds: ['p-aim-create'], visionIds: ['p-v6'], weight: 3 },
      { label: 'A mix', aimIds: ['p-aim-create'], weight: 2 },
      { label: 'Mostly create', visionIds: ['p-v6'], weight: 1 },
    ],
  },
  {
    prompt: 'What matters more to you this season?',
    options: [
      { label: 'Building a skill on purpose', visionIds: ['p-v6'], deityIds: ['mnemosyne'], weight: 3 },
      { label: 'Understanding myself more honestly', visionIds: ['p-v3'], deityIds: ['psyche'], weight: 3 },
      { label: 'Both, equally', deityIds: ['mnemosyne'], weight: 1 },
    ],
  },
  {
    prompt: 'Which sector deity\'s domain feels most needed in your life right now?',
    options: [
      { label: 'Learning and memory — actually building skill', deityIds: ['mnemosyne'], weight: 3 },
      { label: 'Reflection and inner ownership — knowing myself', deityIds: ['psyche'], weight: 3 },
      { label: 'Both equally', deityIds: ['psyche'], weight: 1 },
    ],
  },
];

// ── E · ECONOMIC ──────────────────────────────────────────────────────

const ECONOMIC_SURVEY: SurveyQuestion[] = [
  {
    prompt: 'Which part of your economic life is weakest right now?',
    options: [
      { label: 'I don\'t track my money or protect what I\'ve built', aimIds: ['e-aim-money', 'e-aim-protect'], deityIds: ['plutus'], visionIds: ['e-v2', 'e-v6'], weight: 3 },
      { label: 'I\'m not investing in my future, just my present', aimIds: ['e-aim-invest'], visionIds: ['e-v3'], weight: 3 },
      { label: 'My career and worth aren\'t advancing the way they should', aimIds: ['e-aim-career', 'e-aim-value'], deityIds: ['kerdos'], visionIds: ['e-v5'], weight: 3 },
      { label: 'Everything I earn depends entirely on my direct time', aimIds: ['e-aim-streams'], visionIds: ['e-v1', 'e-v4'], weight: 3 },
    ],
  },
  {
    prompt: 'How closely do you actually track what you spend?',
    options: [
      { label: 'No idea what I spent last week', aimIds: ['e-aim-money'], deityIds: ['plutus'], weight: 3 },
      { label: 'Roughly, not precisely', aimIds: ['e-aim-money'], weight: 2 },
      { label: 'Fairly close, most weeks', aimIds: ['e-aim-money'], weight: 1 },
      { label: 'Dialed in — tracked precisely, every time', deityIds: ['plutus'], weight: 1 },
    ],
  },
  {
    prompt: 'How consistently do you put money toward your long-term future?',
    options: [
      { label: 'Not currently — nothing going toward the future', aimIds: ['e-aim-invest'], deityIds: ['plutus'], weight: 3 },
      { label: 'A little, inconsistently', aimIds: ['e-aim-invest'], weight: 2 },
      { label: 'Fairly regular contributions', aimIds: ['e-aim-invest'], weight: 1 },
      { label: 'Dialed in — systematic and on schedule', deityIds: ['plutus'], weight: 1 },
    ],
  },
  {
    prompt: 'How much does your work actually advance your position versus just fill your time?',
    options: [
      { label: 'Mostly fills time — no real advance', aimIds: ['e-aim-career', 'e-aim-value'], deityIds: ['kerdos'], weight: 3 },
      { label: 'Some of both', aimIds: ['e-aim-career'], weight: 2 },
      { label: 'Mostly advances me, most weeks', aimIds: ['e-aim-value'], weight: 1 },
      { label: 'Dialed in — every project moves me forward', deityIds: ['kerdos'], weight: 1 },
    ],
  },
  {
    prompt: 'Do you know exactly what you spent last week?',
    options: [
      { label: 'No idea', aimIds: ['e-aim-money'], deityIds: ['plutus'], weight: 3 },
      { label: 'Roughly', aimIds: ['e-aim-money'], weight: 2 },
      { label: 'Yes, tracked precisely', deityIds: ['plutus'], weight: 1 },
    ],
  },
  {
    prompt: 'Are you contributing to something long-term — retirement, education, an asset?',
    options: [
      { label: 'Not currently', aimIds: ['e-aim-invest'], deityIds: ['plutus'], weight: 3 },
      { label: 'A little, inconsistently', aimIds: ['e-aim-invest'], weight: 2 },
      { label: 'Yes, systematically', deityIds: ['plutus'], weight: 1 },
    ],
  },
  {
    prompt: 'Do you feel like your work advances your position, or just fills your time?',
    options: [
      { label: 'Mostly fills time, no real advance', aimIds: ['e-aim-career'], deityIds: ['kerdos'], weight: 3 },
      { label: 'Some of both', aimIds: ['e-aim-career'], weight: 2 },
      { label: 'Every project moves me forward', deityIds: ['kerdos'], weight: 1 },
    ],
  },
  {
    prompt: 'When was the last time you invested time in a skill with real professional return?',
    options: [
      { label: 'Can\'t remember', aimIds: ['e-aim-career'], deityIds: ['kerdos'], weight: 3 },
      { label: 'A while back', aimIds: ['e-aim-career'], weight: 2 },
      { label: 'Recently, it\'s ongoing', deityIds: ['kerdos'], weight: 1 },
    ],
  },
  {
    prompt: 'Do you know what you\'re actually worth in the market, and do you ask for it?',
    options: [
      { label: 'No idea, and I don\'t ask', aimIds: ['e-aim-value'], deityIds: ['kerdos'], weight: 3 },
      { label: 'I have a rough sense', aimIds: ['e-aim-value'], weight: 2 },
      { label: 'Yes, and I negotiate accordingly', deityIds: ['kerdos'], weight: 1 },
    ],
  },
  {
    prompt: 'Is your income dependent entirely on your direct time, or do you have anything that earns without you?',
    options: [
      { label: 'Entirely dependent on my time', aimIds: ['e-aim-streams'], visionIds: ['e-v1'], weight: 3 },
      { label: 'A small start on something else', aimIds: ['e-aim-streams'], weight: 2 },
      { label: 'I have real systems earning independently', visionIds: ['e-v4'], weight: 1 },
    ],
  },
  {
    prompt: 'When did you last explore a new income stream or opportunity?',
    options: [
      { label: 'Never really have', aimIds: ['e-aim-streams'], deityIds: ['kerdos'], weight: 3 },
      { label: 'I\'ve thought about it, not acted', aimIds: ['e-aim-streams'], weight: 2 },
      { label: 'Actively exploring right now', deityIds: ['kerdos'], weight: 1 },
    ],
  },
  {
    prompt: 'What does "more options every year, fewer dependencies" mean for your life right now?',
    options: [
      { label: 'The opposite is happening — more dependency', visionIds: ['e-v3'], deityIds: ['plutus'], weight: 3 },
      { label: 'Roughly holding steady', visionIds: ['e-v3'], weight: 2 },
      { label: 'Already true, and growing', visionIds: ['e-v3'], weight: 1 },
    ],
  },
  {
    prompt: 'How do you feel about your foundation — savings, assets, safety net?',
    options: [
      { label: 'Thin, I worry about it', visionIds: ['e-v2'], deityIds: ['plutus'], weight: 3 },
      { label: 'Building, not there yet', visionIds: ['e-v2'], weight: 2 },
      { label: 'Solid, works independently of my time', visionIds: ['e-v2'], weight: 1 },
    ],
  },
  {
    prompt: 'Do you reinvest earnings into higher-leverage skills or assets, or does it just get spent?',
    options: [
      { label: 'It just gets spent', aimIds: ['e-aim-value'], deityIds: ['kerdos'], weight: 3 },
      { label: 'A little gets reinvested', aimIds: ['e-aim-value'], weight: 2 },
      { label: 'Reinvestment is deliberate and consistent', deityIds: ['kerdos'], weight: 1 },
    ],
  },
  {
    prompt: 'How much of your income comes from a craft or skill you\'ve deliberately built?',
    options: [
      { label: 'Not much, it feels replaceable', visionIds: ['e-v5'], deityIds: ['kerdos'], weight: 3 },
      { label: 'Some, I want more', visionIds: ['e-v5'], weight: 2 },
      { label: 'Most of it, and it\'s valuable', visionIds: ['e-v5'], weight: 1 },
    ],
  },
  {
    prompt: 'When a professional opportunity goes quiet, do you follow up?',
    options: [
      { label: 'No, I let it fade', aimIds: ['e-aim-streams'], deityIds: ['kerdos'], weight: 3 },
      { label: 'Sometimes', aimIds: ['e-aim-streams'], weight: 2 },
      { label: 'Always, I close the loop', deityIds: ['kerdos'], weight: 1 },
    ],
  },
  {
    prompt: 'What kind of financial decisions do you regret most?',
    options: [
      { label: 'Not tracking spending closely enough', aimIds: ['e-aim-money'], deityIds: ['plutus'], weight: 3 },
      { label: 'Not investing in myself sooner', aimIds: ['e-aim-career'], deityIds: ['kerdos'], weight: 3 },
      { label: 'Not building a safety net earlier', aimIds: ['e-aim-protect'], deityIds: ['plutus'], weight: 3 },
    ],
  },
  {
    prompt: 'What does "wealth built on decisions, not luck" require of you right now?',
    options: [
      { label: 'Actually tracking and planning my money', visionIds: ['e-v6'], deityIds: ['plutus'], weight: 3 },
      { label: 'Building a craft valuable enough to always earn', visionIds: ['e-v6'], deityIds: ['kerdos'], weight: 3 },
      { label: 'Both, in equal measure', visionIds: ['e-v6'], weight: 1 },
    ],
  },
  {
    prompt: 'What matters more to you this season?',
    options: [
      { label: 'Getting my money actively tracked and managed', deityIds: ['plutus'], weight: 3 },
      { label: 'Growing my earning power and craft', deityIds: ['kerdos'], weight: 3 },
      { label: 'Both, equally', deityIds: ['plutus'], weight: 1 },
    ],
  },
  {
    prompt: 'Which sector deity\'s domain feels most needed in your life right now?',
    options: [
      { label: 'Money and assets — control over what I already have', deityIds: ['plutus'], weight: 3 },
      { label: 'Earning and craft — growing what comes in', deityIds: ['kerdos'], weight: 3 },
      { label: 'Both equally', deityIds: ['kerdos'], weight: 1 },
    ],
  },
];

export const SECTOR_SURVEY: Record<Sector, SurveyQuestion[]> = {
  S: SOCIAL_SURVEY,
  H: HEALTH_SURVEY,
  A: ADMIN_SURVEY,
  P: PERSONAL_SURVEY,
  E: ECONOMIC_SURVEY,
};

/** Tally weighted votes from chosen survey options into a ranked suggestion. */
export function foldSectorSurvey(_sector: Sector, chosen: SurveyOption[]): SectorSuggestion {
  const visionScores = new Map<string, number>();
  const deityScores = new Map<string, number>();
  const aimScores = new Map<string, number>();

  const tally = (scores: Map<string, number>, ids: string[] | undefined, weight: number) => {
    for (const id of ids ?? []) scores.set(id, (scores.get(id) ?? 0) + weight);
  };

  for (const opt of chosen) {
    tally(visionScores, opt.visionIds, opt.weight);
    tally(deityScores, opt.deityIds, opt.weight);
    tally(aimScores, opt.aimIds, opt.weight);
  }

  const top = (scores: Map<string, number>): string | null => {
    let bestId: string | null = null;
    let bestScore = -Infinity;
    for (const [id, score] of scores) {
      if (score > bestScore) {
        bestScore = score;
        bestId = id;
      }
    }
    return bestId;
  };

  const topN = (scores: Map<string, number>, n: number): string[] =>
    [...scores.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, n)
      .map(([id]) => id);

  return {
    visionId: top(visionScores),
    deityId: top(deityScores),
    aimIds: topN(aimScores, 3),
  };
}
